/**
 * door-description-builder.js
 * ---------------------------------------------------------------------------
 * Builds the human-readable configuration description for Vintage Doors
 * products, per the "Vintage Doors - Description Order based on category" spec.
 *
 * SCOPE (v4, merged): every category in the spec
 *   section 1 - Interior / Exterior doors
 *   section 2 - Screen / Storm doors (incl. Arch & Round Top)
 *   section 3 - Porch panels (incl. Design Your Own ordinal openings)
 *
 *   var text = window.DoorDescription.build({
 *     config:       it.options,              // saved config OR live config
 *     schema:       window.__doorConfigSchema,
 *     productTitle: 'Classic Screen & Storm Door',
 *     productType:  'Screen And Storm Doors' // category is inferred from this
 *   });
 *
 * Dimensions resolve in this order: ctx.dims override -> saved config keys
 * (decimals, pattern-matched) -> live configurator DOM.
 *
 * This file supersedes door-description-porch.js - delete that asset.
 *
 * Porch measurements are read from the SAVED CONFIG only. Whichever keys the
 * configurator persists for the openings are matched by pattern in
 * DIM_PATTERNS / buildPorchOpenings; sill, astragal and panel stops come from
 * the KEYS.sill / porchAstragal / porchStops options.
 * ---------------------------------------------------------------------------
 */
(function (root) {
  'use strict';

  /* =======================================================================
   * 1. CONFIG KEYS  (from custom.door_config_options metaobjects)
   * ===================================================================== */
  var KEYS = {
    doorUnitDesign: 'door_unit_design',
    /* wood option id differs by category: storm/porch vs int/ext */
    wood: ['wood_type_storm_porch', 'wood_type'],
    inserts: 'screen_and_storm_inserts',
    screenType: 'screen_type',
    stormGlass: 'storm_glass_type',
    frameColor: 'screen_storm_insert_frame_color',
    sidelightDesign: 'sidelight_design',
    sidelightStyle: 'sidelight_style',
    sidelightLocation: 'sidelight_location',
    transomDesign: 'transom_design',
    transomStyle: 'transom_style',
    measurementType: 'measurement_type',
    preHung: 'pre_hung',
    hingeFinish: 'hinge_finish',
    doorSweep: 'door_sweep',
    doorSealKit: 'door_seal_kit',
    priming: 'priming',
    petDoor: 'pet_door',
    swingDirection: 'swing_direction',
    topStyle: ['top_style_storm_porch', 'top_style'],
    /* interior / exterior only */
    glassType: 'glass_type',
    shelf: ['shelf', 'optional_shelf'],
    panelProfile: 'panel_profile',
    locksetPrep: 'lockset_prep',
    stileRail: 'stile_and_rail_profile',
    appliedMolding: 'applied_molding',
    sdlProfile: 'sdl_profile',
    doorLocation: 'door_location',
    /* porch panel only (not yet present as config options) */
    sill: 'sill',
    porchAstragal: 'porch_panel_astragal',
    porchStops: ['porch_panel_stops', 'panel_stops']
  };

  /* -----------------------------------------------------------------------
   * Dimensions are NOT config options. They are int + fraction <select>
   * pairs in the DOM, one set per measurement type. The fraction selects
   * store decimals (0.4375) but display fractions ("7/16"), so the option
   * TEXT is what we want. Thickness is a single select whose text is
   * already "1 1/8" etc.
   * --------------------------------------------------------------------- */
  var DIM_DOM = {
    exact_door_size: {
      door_width: ['exact-door-width-int', 'exact-door-width-frac'],
      door_height: ['door_height', 'door_height_fraction'],
      thickness: ['exact-door-thickness-frac'],
      jamb_width: ['exact-pre-hung-int', 'exact-pre-hung-frac']
    },
    pre_hung_unit_size: {
      door_width: ['exact-door-width-int', 'exact-door-width-frac'],
      door_height: ['door_height', 'door_height_fraction'],
      thickness: ['exact-door-thickness-frac'],
      jamb_width: ['exact-pre-hung-int', 'exact-pre-hung-frac']
    },
    finished_opening_size: {
      door_width: ['finished-width-int', 'finished-width-frac'],
      door_height: ['finished-height-int', 'finished-height-frac'],
      thickness: ['door-thickness'],
      jamb_width: ['exact-pre-hung-int', 'exact-pre-hung-frac']
    },
    finished_opening: {
      door_width: ['finished-width-int', 'finished-width-frac'],
      door_height: ['finished-height-int', 'finished-height-frac'],
      thickness: ['door-thickness'],
      jamb_width: ['exact-pre-hung-int', 'exact-pre-hung-frac']
    },
    rough_opening_size: {
      door_width: ['rough-width-int', 'rough-width-frac'],
      door_height: ['rough-height-int', 'rough-height-frac'],
      thickness: ['rough-door-thickness'],
      jamb_width: ['rough-pre-hung-int', 'rough-pre-hung-frac']
    }
  };

  /* Shared across every measurement type. */
  var DIM_DOM_SHARED = {
    sidelight_width: ['sidelight_width', 'sidelight_width_fraction'],
    transom_height: ['panel-transom-height-int', 'panel-transom-height-frac'],
    height_a: ['height-a-int', 'height-a-frac'],
    height_b: ['height-b-int', 'height-b-frac']
  };

  /* -----------------------------------------------------------------------
   * A SAVED config (quote cart item, My Projects row) carries the same
   * dimensions keyed by DOM element id -- but as DECIMALS, and with a
   * schema-index suffix that shifts per product:
   *
   *   door_height: "80", door_height_fraction: "0",
   *   panel-door-width-int__37_0: "36", panel-door-width-frac__37_1: "0",
   *   panel-door-thickness-frac__39_0: "1.125"
   *
   * So keys are matched by pattern, and decimals are rendered as fractions.
   * --------------------------------------------------------------------- */
  var DIM_PATTERNS = {
    door_width: {
      int: [/^door_width$/i, /(^|[-_])door[-_]width[-_]int/i, /(^|[-_])(finished|rough)[-_]width[-_]int/i],
      frac: [/(^|[-_])door[-_]width[-_]frac/i, /(^|[-_])(finished|rough)[-_]width[-_]frac/i]
    },
    door_height: {
      int: [/^door_height$/i, /(^|[-_])door[-_]height[-_]int/i, /(^|[-_])(finished|rough)[-_]height[-_]int/i],
      frac: [/^door_height_fraction$/i, /(^|[-_])door[-_]height[-_]frac/i, /(^|[-_])(finished|rough)[-_]height[-_]frac/i]
    },
    thickness: {
      /* exact-door-thickness-frac, rough-door-thickness, door-thickness,
       * panel-door-thickness-frac__39_0 ... */
      whole: [
        /^(exact|rough|finished|panel)?[-_]?door[-_]thickness/i,
        /door[-_]thickness/i,
        /thickness/i
      ]
    },
    jamb_width: {
      int: [/pre[-_]hung[-_]int/i, /^jamb_width$/i],
      frac: [/pre[-_]hung[-_]frac/i]
    },
    sidelight_width: {
      int: [/^sidelight_width$/i, /sidelight[-_]width[-_]int/i],
      frac: [/^sidelight_width_fraction$/i, /sidelight[-_]width[-_]frac/i]
    },
    transom_height: {
      int: [/transom[-_]height[-_]int/i, /^transom_height$/i],
      frac: [/transom[-_]height[-_]frac/i]
    },
    height_a: { int: [/^height[-_]a([-_]int)?$/i], frac: [/^height[-_]a[-_]frac/i] },
    height_b: { int: [/^height[-_]b([-_]int)?$/i], frac: [/^height[-_]b[-_]frac/i] },
    brick_molding_thickness: { whole: [/brick[-_]molding[-_]thickness/i] },
    brick_molding_width: { whole: [/brick[-_]molding[-_]width/i] }
  };

  /* =======================================================================
   * 2. RULE TABLES  (spec "Description - Various Logics")
   * ===================================================================== */

  /* Values that must never appear in the description. */
  var SKIP_VALUES = [
    'none', 'no astragal', 'no sill', 'no shelf', 'no door sweep',
    'no door stop', 'no priming', 'no lock prep', 'please select', 'select',
    'add a pet door',
    'no', 'n/a', '--', 'null', 'undefined'
  ];

  /* Config keys that must never be rendered. */
  var SKIP_KEYS = [
    'qty', 'quantity', 'additional_cost', 'astragal_price', 'crating_price',
    'crating_weight', 'input_sidelights_chk', 'input_porch_chk1'
  ];

  /* Wood species location warnings (spec table: Wood Species). */
  var WOOD_PROTECTED = [
    /* named in the spec */
    'cherry', 'red_oak', 'maple', 'knotty_pine', 'knotty_alder',
    /* added on the Shopify site after the spec was written */
    'ash_storm_porch', 'eastern_white_pine', 'southern_yellow_pine'
  ];
  var WOOD_COVERED = ['poplar'];
  var WOOD_SUFFIX_PROTECTED = ' (Only Suitable for Protected Locations)';
  var WOOD_SUFFIX_COVERED = ' (Only Suitable for Completely Covered Locations)';

  /* screen_and_storm_inserts -> how it prints, and which door-name suffix it drives.
   * nameKind: 'screen' | 'storm' | 'screen_storm'
   * The old site split this across "Screen/Storm Function" + "Storm Type";
   * the Shopify option encodes both in one value. */
  var INSERT_RULES = {
    screen_and_storm_inserts_choice: { text: 'Removable Inserts', nameKind: 'screen_storm', screen: true, storm: true },
    top_and_bottom_inserts: { text: 'Top & Bottom Inserts', nameKind: 'screen_storm', screen: true, storm: true },
    screen_insert_only: { text: 'Screen Only', nameKind: 'screen', screen: true, storm: false },
    storm_glass_only: { text: 'Storm Glass Only', nameKind: 'storm', screen: false, storm: true },
    fixed_screen: { text: 'Permanent Screen Only', nameKind: 'screen', screen: true, storm: false, permanentScreen: true },
    fixed_glass: { text: 'Fixed Glass Only', nameKind: 'storm', screen: false, storm: true, fixedGlass: true }
  };

  /* storm_glass_type -> insert phrasing (spec table: Storm type / Screen type). */
  var STORM_GLASS_TEXT = {
    clear_tempered: 'Clear Tempered Glass Storm Insert',
    clear_low_e: 'Energy Efficient Low E Glass Insert'
  };
  var STORM_GLASS_FIXED_TEXT = {
    clear_tempered: 'Fixed Clear Tempered Glass',
    clear_low_e: 'Fixed Energy Efficient Low E Glass'
  };

  var DOOR_SWEEP_TEXT = { matching_wood_door_sweep: 'Door Sweep' };

  var MEASUREMENT_LABELS = {
    exact_door_size: 'Exact Door Size',
    pre_hung_unit_size: 'Pre-Hung Unit Size',
    rough_opening_size: 'Rough Opening Size',
    finished_opening_size: 'Finished Opening Size',
    finished_opening: 'Finished Opening Size',
    exact_sidelight_size: 'Exact Sidelight Size',
    exact_transom_size: 'Exact Transom Size',
    exact_gate_size: 'Exact Gate Size',
    exact_panel_size: 'Exact Panel Size'
  };

  var SIDELIGHT_LOCATION_TEXT = {
    left_only: 'On Left Only',
    right_only: 'On Right Only',
    both_sides: 'On Both Sides'
  };

  /* Constant text blocks. */
  var TEXT = {
    verifyFinish: 'Verify Finish',
    verifySwing: 'Verify Swing Direction',
    dutchBolt: 'Dutch Door Bolt (Verify Finish)',
    templateRequired: 'Template is Required',
    brickMolding: 'Pre-Hung on Matching Brick Molding:',
    premiumThickness: 'Premium Thickness',
    upgradeWidth: 'Upgrade Required Due To Oversized Width',
    upgradeHeight: 'Upgrade Required Due To Oversized Height',
    upgradeBoth: 'Upgrade Required Due To Oversized Width & Height'
  };

  /* Which pre_hung values append " Unit" to the product name.
   * Spec table 1 lists both jamb and brick molding, but sample 2.2.2
   * ("Chippendale Round Top Screen & Storm Door", pre-hung on brick molding)
   * has no "Unit" — samples win, so brick molding is excluded here.
   * Add 'pre_hung_on_brick_molding' to this list to follow the table instead. */
  var UNIT_SUFFIX_PRE_HUNG = ['pre_hung_on_jamb'];

  var PREMIUM_THICKNESS = '2 1/4';
  var OVERSIZE_WIDTH = 40;
  var OVERSIZE_HEIGHT = 90;

  /* ---------------------------------------------------------------------
   * INTERIOR / EXTERIOR rule tables (spec section 1)
   * ------------------------------------------------------------------- */

  /* Product name suffix by category + door setup (spec table: Product Name). */
  var NAME_SUFFIX = {
    interior: { preHung: 'Interior Door Unit', slab: 'Interior Door - SLAB ONLY' },
    exterior: { preHung: 'Entrance Unit', slab: 'Entry Door - SLAB ONLY' }
  };

  /* The Int / Ext token from the old site. On Shopify interior and exterior
   * are separate products and the word is already in the computed name
   * ("Entrance Unit" / "Interior Door Unit"), so it is off by default.
   * Set to true to print it as the spec sample does. */
  var SHOW_INT_EXT_TOKEN = false;

  /* Glass type (spec table: Glass Type). The standard glass prints plain,
   * everything else is an upgrade. */
  var GLASS_STANDARD = 'clear_tempered';
  var GLASS_SUFFIX_STANDARD = ' Glass';
  var GLASS_SUFFIX_UPGRADE = ' Glass *Upgrade*';

  /* Optional shelf (spec table: Optional Shelf). */
  var SHELF_ACCENT = ['cs1', 's1', 's2', 's2m'];
  var SHELF_PLAIN = ['s3m'];

  /* Astragal (spec table: Astragal). Double doors only, and never for a
   * pocket door. */
  var ASTRAGAL_TEXT = {
    exterior: '5/8" T-Astragal with Mortise Flush Bolts',
    interior: '5/8" T-Astragal with Mortise Flush Bolts',
    screen_storm: '3/8" Thick Center T-Astragal',
    porch: '3/8" Thick Center T-Astragal'
  };
  var NO_ASTRAGAL_SWINGS = ['no_swing_pocket_door'];

  /* Constant threshold / casing text (spec table: Exterior / Pre hung msg). */
  var THRESHOLD = 'Adjustable Mahogany Threshold';
  var CASING_SQUARE = '2 1/2" Wide Exterior Brick Molding';
  var CASING_ROUND_TOP = '3" Wide Flat Round Top Exterior Casing with Softened Edge';
  var CASING_SCREEN_ARCH = '3" Wide Flat Exterior Casing with Softened Edge';

  var DUTCH_BOLT_SUFFIX = ' Dutch Door Bolt';

  var DUTCH_MATCHERS = [
    'dutch door', 'dutch louver', 'dutch entrance', 'double dutch', 'dd202rt'
  ];

  /* =======================================================================
   * 3. HELPERS
   * ===================================================================== */

  function isBlank(v) {
    return v == null || String(v).trim() === '';
  }

  function norm(v) {
    return String(v == null ? '' : v).trim().toLowerCase();
  }

  function isSkippable(v) {
    if (isBlank(v)) return true;
    var n = norm(v);
    if (SKIP_VALUES.indexOf(n) !== -1) return true;
    // "No Beveled Glass", "No Priming", "No Door Sweep", "No Door Stop", etc.
    if (/^no\s+/i.test(String(v).trim())) return true;
    if (/^none(\s|$)/i.test(String(v).trim())) return true;
    return false;
  }

  function shortenExposureLabel(label) {
    var s = String(label == null ? '' : label).trim();
    if (!s) return '';
    var m = s.match(/^(Full Exposure|Partial Exposure|Protected Location)\b/i);
    if (m) {
      var map = {
        'full exposure': 'Full Exposure',
        'partial exposure': 'Partial Exposure',
        'protected location': 'Protected Location'
      };
      return map[String(m[1]).toLowerCase()] || m[1];
    }
    return s;
  }

  /** Parse "44 7/16" / "44.5" / "44" into a number. */
  function toNumber(v) {
    if (v == null) return NaN;
    var s = String(v).trim();
    if (!s) return NaN;
    var m = /^(\d+)\s+(\d+)\s*\/\s*(\d+)$/.exec(s);
    if (m) return parseInt(m[1], 10) + parseInt(m[2], 10) / parseInt(m[3], 10);
    m = /^(\d+)\s*\/\s*(\d+)$/.exec(s);
    if (m) return parseInt(m[1], 10) / parseInt(m[2], 10);
    var n = parseFloat(s.replace(/[^\d.]/g, ''));
    return isNaN(n) ? NaN : n;
  }

  /**
   * Reader over config + schema. Returns raw value and display label for a key.
   */
  function makeReader(config, schema) {
    var byId = {};
    (schema || []).forEach(function (opt) {
      if (opt && opt.id) byId[opt.id] = opt;
    });

    /** Accepts a key or a list of candidate keys; first non-blank wins. */
    function resolveKey(key) {
      if (Array.isArray(key)) {
        for (var i = 0; i < key.length; i++) {
          if (config && !isBlank(config[key[i]])) return key[i];
        }
        return key[0];
      }
      return key;
    }

    function raw(key) {
      key = resolveKey(key);
      if (!key || SKIP_KEYS.indexOf(key) !== -1) return '';
      var v = config ? config[key] : '';
      if (Array.isArray(v)) v = v.length ? v[0] : '';
      return v == null ? '' : v;
    }

    function label(key) {
      var v = raw(key);
      if (isBlank(v)) return '';
      var opt = byId[resolveKey(key)];
      var out = '';
      if (opt && Array.isArray(opt.options)) {
        for (var i = 0; i < opt.options.length; i++) {
          var c = opt.options[i];
          if (String(c.value) === String(v)) {
            out = String(c.label || c.value || v);
            break;
          }
        }
      }
      if (!out) {
        /* No schema hit: prettify the stored value. Underscores only --
         * hyphens are meaningful in style codes like "SSL-08" / "ST-05". */
        out = String(v)
          .replace(/_+/g, ' ')
          .replace(/\s+/g, ' ')
          .trim()
          .replace(/\b\w/g, function (ch) { return ch.toUpperCase(); });
      }
      var resolvedKey = String(resolveKey(key) || '');
      if (resolvedKey === 'door_location_exposure' || /exposure|protected location/i.test(out)) {
        out = shortenExposureLabel(out);
      }
      return out;
    }

    function checked(key) {
      var v = config ? config[resolveKey(key)] : '';
      if (Array.isArray(v)) return v.length > 0;
      if (typeof v === 'boolean') return v;
      var n = norm(v);
      return n !== '' && n !== 'false' && n !== '0' && n !== 'no';
    }

    return { raw: raw, label: label, checked: checked, resolveKey: resolveKey };
  }

  /** Selected option text for a select id, '' if missing / not selected. */
  function selectedText(id) {
    if (typeof document === 'undefined') return '';
    var el = document.getElementById(id);
    if (!el || !el.options || el.selectedIndex < 0) return '';
    var op = el.options[el.selectedIndex];
    return op ? String(op.textContent || op.value || '').trim() : '';
  }

  /** Collapse runs of whitespace: "1  3/4" -> "1 3/4". */
  function tidy(v) {
    return String(v == null ? '' : v).replace(/\s+/g, ' ').trim();
  }

  /** True when a fraction select sits on its zero option ("0", "00", "0/0"). */
  function isZeroFrac(v) {
    var t = tidy(v);
    if (!t) return true;
    if (/^0+$/.test(t)) return true;
    if (t === '0/0') return true;
    var n = parseFloat(t);
    return !isNaN(n) && n === 0;
  }

  /** "36" + "7/16" -> "36 7/16"; a bare "0" fraction is dropped. */
  function readDomPair(ids) {
    if (!ids || !ids.length) return '';
    var whole = selectedText(ids[0]);
    if (!whole) return '';
    if (ids.length === 1) return whole;
    var frac = selectedText(ids[1]);
    if (isZeroFrac(frac)) return tidy(whole);
    return tidy(whole + ' ' + frac);
  }

  var GCD = function (a, b) { return b ? GCD(b, a % b) : a; };

  /** 0.4375 -> "7/16"; 0 -> ""; rounded to the nearest 1/16. */
  function decimalToFraction(v) {
    var n = parseFloat(v);
    if (isNaN(n) || n <= 0) return '';
    var num = Math.round(n * 16);
    if (num <= 0) return '';
    var den = 16;
    var g = GCD(num, den) || 1;
    return (num / g) + '/' + (den / g);
  }

  /** 1.125 -> "1 1/8"; 2 -> "2"; already-formatted "1 1/8" passes through. */
  function decimalToMixed(v) {
    var s = String(v == null ? '' : v).trim();
    if (!s) return '';
    if (/[\s]\d+\s*\/\s*\d+$/.test(s) || /^\d+\s*\/\s*\d+$/.test(s)) return s;
    var n = parseFloat(s);
    if (isNaN(n)) return s;
    var whole = Math.floor(n);
    var frac = decimalToFraction(n - whole);
    if (!frac) return String(whole);
    return whole > 0 ? whole + ' ' + frac : frac;
  }

  /** First config key matching any pattern in the list. */
  function findByPattern(keys, config, patterns) {
    if (!patterns) return null;
    for (var p = 0; p < patterns.length; p++) {
      for (var k = 0; k < keys.length; k++) {
        /* underscore-prefixed keys are internal metadata, never dimensions */
        if (keys[k].charAt(0) === '_') continue;
        if (patterns[p].test(keys[k]) && !isBlank(config[keys[k]])) return config[keys[k]];
      }
    }
    return null;
  }

  /** Resolve a dimension from a saved config object. */
  function readConfigDim(config, name) {
    var spec = DIM_PATTERNS[name];
    if (!spec || !config) return '';
    var keys = Object.keys(config);

    if (spec.whole) {
      var w = findByPattern(keys, config, spec.whole);
      return w == null ? '' : decimalToMixed(w);
    }

    var intVal = findByPattern(keys, config, spec.int);
    if (intVal == null) return '';
    var whole = String(intVal).trim();
    /* An "int" slot that already carries a decimal is a mixed measure. */
    if (/\./.test(whole)) return decimalToMixed(whole);

    var fracVal = findByPattern(keys, config, spec.frac);
    var frac = fracVal == null ? '' : decimalToFraction(fracVal);
    return frac ? whole + ' ' + frac : whole;
  }

  function makeDimReader(config, overrides, measurementType) {
    overrides = overrides || {};
    var domSet = DIM_DOM[norm(measurementType)] || DIM_DOM.exact_door_size;

    return function dim(name) {
      /* 1. explicit override from the caller */
      if (!isBlank(overrides[name])) return String(overrides[name]).trim();

      /* 2. a saved config object (quote cart item / My Projects row) */
      var fromConfig = readConfigDim(config, name);
      if (!isBlank(fromConfig)) return fromConfig;

      /* 3. the live configurator DOM */
      return readDomPair(domSet[name] || DIM_DOM_SHARED[name]);
    };
  }

  /* Strip an existing trailing product-type suffix from the product title so a
   * computed one can be appended. */
  function stripDoorSuffix(title) {
    var t = String(title || '').trim();
    var patterns = [
      /\s*porch\s*panel$/i,
      /\s*(screen\s*(&|and)\s*storm)\s*(door|panel)(\s*unit)?$/i,
      /\s*screen\s*door(\s*unit)?$/i,
      /\s*storm\s*door(\s*unit)?$/i,
      /\s*entrance\s*unit$/i,
      /\s*(interior|exterior|entry)\s*door(\s*with\s*glass)?(\s*-\s*slab\s*only)?$/i,
      /\s*door\s*with\s*glass$/i
    ];
    for (var i = 0; i < patterns.length; i++) {
      if (patterns[i].test(t)) return t.replace(patterns[i], '').trim();
    }
    return t;
  }

  /* =======================================================================
   * 4. RULES
   * ===================================================================== */

  /** Product name (spec table: Product Name). */
  function buildProductName(ctx, r) {
    var base = stripDoorSuffix(ctx.productTitle);
    var insertValue = norm(r.raw(KEYS.inserts));
    var rule = INSERT_RULES[insertValue];
    var kind = rule ? rule.nameKind : 'screen_storm';

    var suffix;
    if (kind === 'screen') suffix = 'Screen Door';
    else if (kind === 'storm') suffix = 'Storm Door';
    else suffix = 'Screen & Storm Door';

    var preHung = norm(r.raw(KEYS.preHung));
    if (UNIT_SUFFIX_PRE_HUNG.indexOf(preHung) !== -1) suffix += ' Unit';

    var name = (base ? base + ' ' : '') + suffix;
    if (norm(r.raw(KEYS.doorUnitDesign)) === 'double_door') name = 'Double ' + name;
    return name;
  }

  /** Wood species + location warning. */
  function buildWood(r) {
    var value = norm(r.raw(KEYS.wood));
    var label = r.label(KEYS.wood);
    if (isSkippable(label)) return '';
    if (WOOD_PROTECTED.indexOf(value) !== -1) return label + WOOD_SUFFIX_PROTECTED;
    if (WOOD_COVERED.indexOf(value) !== -1) return label + WOOD_SUFFIX_COVERED;
    return label;
  }

  /** Screen insert phrasing. */
  function buildScreenInsert(r, rule) {
    if (!rule || !rule.screen) return '';
    var label = r.label(KEYS.screenType);
    if (isSkippable(label)) return '';
    if (rule.permanentScreen) return 'Permanent ' + label + ' Screen';
    return label + ' Screen Insert';
  }

  /** Storm insert phrasing. */
  function buildStormInsert(r, rule) {
    if (!rule || !rule.storm) return '';
    var value = norm(r.raw(KEYS.stormGlass));
    var label = r.label(KEYS.stormGlass);
    if (isSkippable(label)) return '';
    if (rule.fixedGlass) {
      return STORM_GLASS_FIXED_TEXT[value] || ('Fixed ' + label + ' Glass');
    }
    return STORM_GLASS_TEXT[value] || (label + ' Glass Storm Insert');
  }

  /** Sidelight / transom sentence + their sizes. */
  function buildSidelightTransom(r, dim) {
    var out = [];
    var isDouble = norm(r.raw(KEYS.doorUnitDesign)) === 'double_door';
    var unitWord = isDouble ? 'Double Door' : 'Single Door';

    var hasSidelight = r.checked(KEYS.sidelightDesign) && !isSkippable(r.raw(KEYS.sidelightStyle));
    var hasTransom = r.checked(KEYS.transomDesign) && !isSkippable(r.raw(KEYS.transomStyle));
    if (!hasSidelight && !hasTransom) return out;

    var parts = [unitWord, 'with'];
    if (hasSidelight) {
      var sName = r.label(KEYS.sidelightStyle);
      var loc = SIDELIGHT_LOCATION_TEXT[norm(r.raw(KEYS.sidelightLocation))] || '';
      parts.push(sName + ' Sidelight' + (loc ? ' ' + loc : ''));
    }
    if (hasSidelight && hasTransom) parts.push('and');
    if (hasTransom) parts.push(r.label(KEYS.transomStyle) + ' Transom Above');
    out.push(parts.join(' '));

    if (hasSidelight) {
      var sw = dim('sidelight_width');
      if (sw) out.push('Exact Sidelight Width: ' + sw + 'w');
    }
    if (hasTransom) {
      var th = dim('transom_height');
      if (th) out.push('Exact Transom Height: ' + th + 'h');
    }
    return out;
  }

  /** Measurement line: "<Type>: 44 7/16w / 80h / 2 1/4t <upgrade text>" */
  function buildMeasurement(r, dim, ctx) {
    var out = [];
    var typeValue = norm(r.raw(KEYS.measurementType));
    var typeLabel = MEASUREMENT_LABELS[typeValue] || r.label(KEYS.measurementType);
    var w = dim('door_width');
    var h = dim('door_height');
    var t = dim('thickness');
    var isArch = !!ctx.isArch;
    var ha = dim('height_a');
    var hb = dim('height_b');

    if (!typeLabel && !w && !h) return out;

    var line = (typeLabel ? typeLabel + ': ' : '');
    var bits = [];
    if (w) bits.push(w + 'w');

    if (isArch && ha && hb) {
      bits.push(ha + 'h (Shoulder Height)');
      bits.push(hb + 'h (Peak Height)');
    } else if (h) {
      bits.push(h + 'h');
    }

    /* Thickness, with the premium / oversize upgrade wording. */
    if (t) {
      var tText = t + 't';
      var isPremium = norm(t) === norm(PREMIUM_THICKNESS);
      var effectiveWidth = toNumber(w);
      if (norm(r.raw(KEYS.doorUnitDesign)) === 'double_door' && !isNaN(effectiveWidth)) {
        effectiveWidth = effectiveWidth / 2;
      }
      var effectiveHeight = toNumber(isArch && hb ? hb : h);
      var overW = !isNaN(effectiveWidth) && effectiveWidth >= OVERSIZE_WIDTH;
      var overH = !isNaN(effectiveHeight) && effectiveHeight >= OVERSIZE_HEIGHT;

      var upgrade = '';
      /* Checked most-specific first so the 40 / 90 boundary is deterministic. */
      if (overW && overH) upgrade = TEXT.upgradeBoth;
      else if (overW) upgrade = TEXT.upgradeWidth;
      else if (overH) upgrade = TEXT.upgradeHeight;

      if (isPremium) tText += ' ' + TEXT.premiumThickness;
      else if (upgrade) tText += ' Thickness';
      if (upgrade) tText += ' ' + upgrade;
      bits.push(tText);
    }

    out.push(line + bits.join(' / '));

    /* Arch & round top: template required when the arch is not a true radius. */
    if (isArch) {
      var na = toNumber(ha), nb = toNumber(hb), nw = toNumber(w);
      if (!isNaN(na) && !isNaN(nb) && !isNaN(nw) && (nb - na) !== (nw / 2)) {
        out.push(TEXT.templateRequired);
      }
    }
    return out;
  }

  /** Pre-hung line. */
  function buildPreHung(r, dim) {
    var v = norm(r.raw(KEYS.preHung));
    if (v === 'pre_hung_on_jamb') {
      var jw = dim('jamb_width');
      return jw ? 'Pre-Hung on Matching Jamb: ' + jw + 'w' : 'Pre-Hung on Matching Jamb';
    }
    if (v === 'pre_hung_on_brick_molding') {
      var bt = dim('brick_molding_thickness');
      var bw = dim('brick_molding_width') || '2 1/2';
      var bits = [];
      if (bt) bits.push(bt + 't');
      bits.push(bw + 'w');
      return TEXT.brickMolding + ' ' + bits.join(' / ');
    }
    return '';
  }

  /** Hinges. */
  function buildHinges(r, ctx) {
    var preHung = norm(r.raw(KEYS.preHung));
    var isPreHung = preHung === 'pre_hung_on_jamb' || preHung === 'pre_hung_on_brick_molding';
    if (!isPreHung) return '';

    var finish = r.raw(KEYS.hingeFinish);
    var title = norm(ctx.productTitle);
    var isDutch = DUTCH_MATCHERS.some(function (m) { return title.indexOf(m) !== -1; });

    if (isSkippable(finish)) {
      return isDutch ? TEXT.dutchBolt : TEXT.verifyFinish;
    }
    return r.label(KEYS.hingeFinish) + ' Hinges';
  }

  /** Simple pass-through option, skipped when its value is on the skip list. */
  function plain(r, key) {
    var v = r.raw(key);
    if (isSkippable(v)) return '';
    var label = r.label(key);
    return isSkippable(label) ? '' : label;
  }

  /* ---------------------------------------------------------------------
   * INTERIOR / EXTERIOR + PORCH rules
   * ------------------------------------------------------------------- */

  /** Which spec section applies. */
  function resolveCategory(ctx) {
    var hint = norm(ctx.category) + ' ' + norm(ctx.productType) + ' ' + norm(ctx.productTitle);
    /* Hardware and Millwork have their own landing-page flow and no spec
     * section; Gates are not in the spec either. Match them first so they
     * can never fall through to a door assembler. */
    if (/hardware|millwork/.test(hint)) return '';
    if (/porch/.test(hint)) return 'porch';
    if (/screen|storm/.test(hint)) return 'screen_storm';
    if (/interior/.test(hint)) return 'interior';
    if (/exterior|entrance|entry/.test(hint)) return 'exterior';
    /* Unrecognised: refuse rather than guess. The caller falls back to the
     * legacy label/value summary. */
    return '';
  }

  function isArchTitle(ctx) {
    return /round\s*top|arch/i.test(String(ctx.productTitle || '')) ||
      /arch|round/.test(norm(ctx.category));
  }

  /** Interior / exterior product name (spec table: Product Name). */
  function buildIntExtName(ctx, r, category) {
    var base = stripDoorSuffix(ctx.productTitle);
    var map = NAME_SUFFIX[category] || NAME_SUFFIX.exterior;
    var preHung = norm(r.raw(KEYS.preHung));
    var isPreHung = preHung === 'pre_hung_on_jamb' || preHung === 'pre_hung_on_brick_molding';
    var name = (base ? base + ' ' : '') + (isPreHung ? map.preHung : map.slab);
    if (norm(r.raw(KEYS.doorUnitDesign)) === 'double_door') name = 'Double ' + name;
    return name;
  }

  /** Porch panel product name: base + "Panel" + screen/storm suffix. */
  function buildPorchName(ctx, r) {
    var base = stripDoorSuffix(ctx.productTitle);
    var rule = INSERT_RULES[norm(r.raw(KEYS.inserts))];
    var kind = rule ? rule.nameKind : 'screen_storm';
    var suffix = kind === 'screen' ? 'Screen Door'
      : kind === 'storm' ? 'Storm Door' : 'Screen & Storm Door';
    var name = (base ? base + ' ' : '') + 'Panel ' + suffix;
    if (norm(r.raw(KEYS.doorUnitDesign)) === 'double_door') name = 'Double ' + name;
    return name;
  }

  function buildGlass(r) {
    var value = norm(r.raw(KEYS.glassType));
    var label = r.label(KEYS.glassType);
    if (isSkippable(label)) return '';
    return label + (value === GLASS_STANDARD ? GLASS_SUFFIX_STANDARD : GLASS_SUFFIX_UPGRADE);
  }

  function buildShelf(r) {
    var value = norm(r.raw(KEYS.shelf));
    var label = r.label(KEYS.shelf);
    if (isSkippable(value) || isSkippable(label)) return '';
    if (SHELF_PLAIN.indexOf(value) !== -1) return label + ' Shelf';
    if (SHELF_ACCENT.indexOf(value) !== -1) return label + ' Accent Shelf';
    return label + ' Shelf';
  }

  function buildAstragal(r, category) {
    if (norm(r.raw(KEYS.doorUnitDesign)) !== 'double_door') return '';
    if (NO_ASTRAGAL_SWINGS.indexOf(norm(r.raw(KEYS.swingDirection))) !== -1) return '';
    return ASTRAGAL_TEXT[category] || '';
  }

  /** Constant threshold + casing text for pre-hung units. */
  function buildThresholdCasing(r, category, isArch) {
    var preHung = norm(r.raw(KEYS.preHung));
    if (preHung !== 'pre_hung_on_jamb' && preHung !== 'pre_hung_on_brick_molding') return [];
    if (category === 'exterior') {
      return [THRESHOLD, isArch ? CASING_ROUND_TOP : CASING_SQUARE];
    }
    if (category === 'screen_storm' && isArch) {
      return [THRESHOLD, CASING_SCREEN_ARCH];
    }
    return [];
  }

  var OPENING_ORDINALS = [
    'First', 'Second', 'Third', 'Fourth', 'Fifth',
    'Sixth', 'Seventh', 'Eighth', 'Ninth', 'Tenth'
  ];

  /**
   * "Design Your Own Porch Panel Project" produces one size per opening:
   *   First Opening Size: 46w / 84h / 1 3/8t / Second Opening Size: ...
   * Keys are matched loosely because the configurator does not expose these
   * fields yet; the shape is taken from the spec sample.
   */
  /* ---------------------------------------------------------------------
   * Porch enclosure openings.
   *
   * The configurator persists these as a structured array on the saved
   * config (see the "Dp" blocks in door-conf2.js / door-quote-cart.liquid):
   *
   *   options.panel_unit_enclosure_openings = [
   *     { width_int, width_frac, height_int, height_frac, thickness,
   *       panels | quantity, trim_system, sill_depth, sill_frac,
   *       doors: [ { door_number, swing_direction, hinge_finish,
   *                  lockset_preparation } ] }, ...
   *   ]
   *
   * Fractions are decimals; camelCase aliases are accepted because both
   * spellings appear in the wild. The value may also arrive JSON-encoded.
   * ------------------------------------------------------------------- */
  function readEnclosureOpenings(config) {
    if (!config) return [];
    var raw = config.panel_unit_enclosure_openings;
    if (isBlank(raw)) raw = config._panel_unit_enclosure_openings;
    if (isBlank(raw)) return [];
    if (typeof raw === 'string') {
      try { raw = JSON.parse(raw); } catch (eParse) { return []; }
    }
    return Array.isArray(raw) ? raw.filter(Boolean) : [];
  }

  function pick(op, a, b) {
    var v = op[a];
    if (isBlank(v)) v = op[b];
    return v == null ? '' : v;
  }

  /** "36" + frac 0.4375 -> "36 7/16". */
  function openingMeasure(whole, frac) {
    if (isBlank(whole)) return '';
    var f = decimalToFraction(frac);
    return tidy(String(whole).trim() + (f ? ' ' + f : ''));
  }

  function isTrimOn(op) {
    var v = pick(op, 'trim_system', 'trimSystem');
    return v === true || v === 'true' || v === '1' || v === 1;
  }

  /* Stops / astragals, mirroring dpPorchStopsAstragalsFromOpening in
   * door-quote-cart.liquid: derived from the opening height, with the
   * top-and-bottom run reduced by the stile allowance. An opening that
   * carries explicit stops / astragals values wins over the calculation. */
  var PORCH_STOP_ALLOWANCE = 56.25;

  function openingHeightInches(op) {
    var whole = parseFloat(pick(op, 'height_int', 'heightInt')) || 0;
    var frac = parseFloat(pick(op, 'height_frac', 'heightFrac')) || 0;
    return whole + frac;
  }

  /** 23.75 -> "23 3/4"; rounded to the nearest 1/16. */
  function inchLabel(n) {
    if (isNaN(n)) return '';
    var rounded = Math.round(n * 16) / 16;
    if (rounded < 0) rounded = 0;
    var whole = Math.floor(rounded + 1e-9);
    var frac = decimalToFraction(rounded - whole);
    return frac ? whole + ' ' + frac : String(whole);
  }

  function porchStopsAstragals(op) {
    var out = { stops: '', astragals: '' };
    if (!op) return out;
    if (!isBlank(op.stops) && !isBlank(op.astragals)) {
      out.stops = String(op.stops);
      out.astragals = String(op.astragals);
      return out;
    }
    var h = openingHeightInches(op);
    if (!h) return out;
    var panels = Math.max(1, parseInt(pick(op, 'panels', 'quantity'), 10) || 1);
    var hLabel = inchLabel(h);
    var tbLabel = inchLabel(Math.max(0, h - PORCH_STOP_ALLOWANCE));
    out.stops = '(4) ' + tbLabel + '" top and bottom; (4) ' + hLabel + '" left and right';
    out.astragals = '(' + panels + ') ' + hLabel + '"';
    return out;
  }

  /** Spec 3.2: "First Opening Size: 46w / 84h / 1 3/8t / ..." per opening. */
  function buildPorchOpenings(config) {
    var openings = readEnclosureOpenings(config);
    if (!openings.length) return buildPorchOpeningsFromLooseKeys(config);

    var out = [];
    openings.forEach(function (op, i) {
      var w = openingMeasure(pick(op, 'width_int', 'widthInt'), pick(op, 'width_frac', 'widthFrac'));
      var h = openingMeasure(pick(op, 'height_int', 'heightInt'), pick(op, 'height_frac', 'heightFrac'));
      var t = op.thickness;
      if (!w && !h) return;

      var bits = [];
      if (w) bits.push(w + 'w');
      if (h) bits.push(h + 'h');
      if (!isBlank(t)) bits.push(decimalToMixed(t) + 't');

      var line = (OPENING_ORDINALS[i] || ('Opening ' + (i + 1))) + ' Opening Size: ' + bits.join(' / ');
      var panels = parseInt(pick(op, 'panels', 'quantity'), 10);
      if (panels > 1) line += ' / ' + panels + ' Panels per Opening';
      out.push(line);

      if (isTrimOn(op)) {
        var sill = openingMeasure(
          isBlank(pick(op, 'sill_depth', 'sillDepth')) ? '5' : pick(op, 'sill_depth', 'sillDepth'),
          pick(op, 'sill_frac', 'sillFrac')
        );
        if (sill) out.push('Sill: ' + sill + '"');
      }

      var sa = porchStopsAstragals(op);
      if (sa.stops) out.push('Panel Stops: ' + sa.stops);
      if (sa.astragals) out.push('Astragals: ' + sa.astragals);

      (op.doors || []).forEach(function (door) {
        if (!door) return;
        /* doors carry an explicit active flag; skip the ones switched off */
        var active = door.active;
        if (active === false || active === 'false' || active === 0 || active === '0') return;
        var dn = pick(door, 'door_number', 'doorNumber');
        if (isBlank(dn)) return;
        var label = 'Door #' + dn + ':';
        var parts = [];
        var swing = pick(door, 'swing_direction', 'swingDirection');
        var hinge = pick(door, 'hinge_finish', 'hingeFinish');
        var lock = pick(door, 'lockset_preparation', 'locksetPreparation');
        if (!isSkippable(swing)) parts.push(humanize(swing));
        if (!isSkippable(hinge)) parts.push(humanize(hinge) + ' Hinges');
        if (!isSkippable(lock)) parts.push(humanize(lock));
        if (parts.length) out.push(label + ' ' + parts.join(' / '));
      });
    });
    return out;
  }

  /** "raised_beveled_panel" -> "Raised Beveled Panel". */
  function humanize(v) {
    return tidy(String(v || '').replace(/[_-]+/g, ' ')).replace(/\b\w/g, function (c) {
      return c.toUpperCase();
    });
  }

  /** Legacy fallback: loose opening_<n>_width_int style keys. */
  function buildPorchOpeningsFromLooseKeys(config) {
    if (!config) return [];
    var keys = Object.keys(config).filter(function (k) {
      return k.charAt(0) !== '_' && /opening|enclosure/i.test(k);
    });
    if (!keys.length) return [];

    var out = [];
    for (var n = 1; n <= OPENING_ORDINALS.length; n++) {
      var wInt = findByPattern(keys, config, [new RegExp('(opening|enclosure)[^0-9]*' + n + '.*width.*int', 'i')]);
      var wFrac = findByPattern(keys, config, [new RegExp('(opening|enclosure)[^0-9]*' + n + '.*width.*frac', 'i')]);
      var hInt = findByPattern(keys, config, [new RegExp('(opening|enclosure)[^0-9]*' + n + '.*height.*int', 'i')]);
      var hFrac = findByPattern(keys, config, [new RegExp('(opening|enclosure)[^0-9]*' + n + '.*height.*frac', 'i')]);
      var th = findByPattern(keys, config, [new RegExp('(opening|enclosure)[^0-9]*' + n + '.*thick', 'i')]);
      if (isBlank(wInt) && isBlank(hInt)) continue;

      var bits = [];
      if (!isBlank(wInt)) bits.push(openingMeasure(wInt, wFrac) + 'w');
      if (!isBlank(hInt)) bits.push(openingMeasure(hInt, hFrac) + 'h');
      if (!isBlank(th)) bits.push(decimalToMixed(th) + 't');
      var line = OPENING_ORDINALS[n - 1] + ' Opening Size: ' + bits.join(' / ');
      var panels = findByPattern(keys, config, [new RegExp('(opening|enclosure)[^0-9]*' + n + '.*panels', 'i')]);
      var pn = panels ? parseInt(panels, 10) : 0;
      if (pn > 1) line += ' / ' + pn + ' Panels per Opening';
      out.push(line);
    }
    return out;
  }

  /** Porch screen type - only when the insert choice includes a screen.
   * The porch sample prints it bare, without " Screen Insert". */
  function buildPorchScreenType(r, rule) {
    if (!rule || !rule.screen) return '';
    var label = r.label(KEYS.screenType);
    if (isSkippable(label)) return '';
    return rule.permanentScreen ? 'Permanent ' + label + ' Screen' : label;
  }

  /** Porch transom: "ST-01 Transom Above" + its height when present. */
  function buildPorchTransom(r, dim) {
    if (!r.checked(KEYS.transomDesign)) return [];
    var style = r.raw(KEYS.transomStyle);
    if (isSkippable(style)) return [];
    var out = [r.label(KEYS.transomStyle) + ' Transom Above'];
    var th = dim('transom_height');
    if (th) out.push('Exact Transom Height: ' + th + 'h');
    return out;
  }

  /** "Black Dutch Door Bolt" tail for dutch products. */
  function buildDutchBolt(r, ctx) {
    var title = norm(ctx.productTitle);
    if (!DUTCH_MATCHERS.some(function (m) { return title.indexOf(m) !== -1; })) return '';
    var preHung = norm(r.raw(KEYS.preHung));
    if (preHung !== 'pre_hung_on_jamb' && preHung !== 'pre_hung_on_brick_molding') return '';
    var finish = r.raw(KEYS.hingeFinish);
    if (isSkippable(finish)) return '';
    return r.label(KEYS.hingeFinish) + DUTCH_BOLT_SUFFIX;
  }

  /* =======================================================================
   * 5. ASSEMBLY
   * ===================================================================== */

  function buildScreenStorm(ctx) {
    ctx = ctx || {};
    var config = ctx.config || {};
    var r = makeReader(config, ctx.schema);
    var dim = makeDimReader(config, ctx.dims, r.raw(KEYS.measurementType));
    var insertRule = INSERT_RULES[norm(r.raw(KEYS.inserts))];

    var parts = [];
    function push(v) {
      if (Array.isArray(v)) { v.forEach(push); return; }
      if (!isBlank(v) && !isSkippable(v)) parts.push(String(v).trim());
    }

    /* Spec 2.1 order. */
    push(buildProductName(ctx, r));                     // Double + Door name
    if (ctx.isArch) push(plain(r, KEYS.topStyle));      // Round / Arch top style
    push(buildWood(r));                                 // Wood type
    push(buildAstragal(r, 'screen_storm'));             // Astragal (double doors)
    push(insertRule ? insertRule.text : '');            // Screen / Storm function
    push(buildScreenInsert(r, insertRule));             // Screen type
    push(buildStormInsert(r, insertRule));              // Storm type
    push(plain(r, KEYS.frameColor));                    // Frame colour
    push(buildSidelightTransom(r, dim));                // Sidelights / transoms
    push(buildMeasurement(r, dim, ctx));                // Sizes + thickness
    push(buildPreHung(r, dim));                         // Pre-hung jamb / brick
    push(buildHinges(r, ctx));                          // Hinge finish
    push(DOOR_SWEEP_TEXT[norm(r.raw(KEYS.doorSweep))] || plain(r, KEYS.doorSweep));
    push(plain(r, KEYS.doorSealKit));                   // Door stops with seals
    push(plain(r, KEYS.priming));                       // Priming
    var pet = plain(r, KEYS.petDoor);                   // Pet door
    if (pet) push('Pet Door Size: ' + pet);
    var swing = r.raw(KEYS.swingDirection);             // Swing direction
    push(isSkippable(swing) ? TEXT.verifySwing : r.label(KEYS.swingDirection));

    return parts.join(' / ');
  }

  function build(ctx) {
    ctx = ctx || {};
    var category = resolveCategory(ctx);
    ctx.isArch = isArchTitle(ctx);
    ctx.resolvedCategory = category;

    if (category === 'interior' || category === 'exterior') return buildIntExt(ctx, category);
    if (category === 'porch') return buildPorch(ctx);
    if (category === 'screen_storm') return buildScreenStorm(ctx);
    /* No spec section covers this product - return nothing so the caller
     * keeps its existing summary rather than showing a wrong description. */
    return '';
  }

  /* Spec section 1: interior / exterior doors. */
  function buildIntExt(ctx, category) {
    var config = ctx.config || {};
    var r = makeReader(config, ctx.schema);
    var dim = makeDimReader(config, ctx.dims, r.raw(KEYS.measurementType));

    var parts = [];
    function push(v) {
      if (Array.isArray(v)) { v.forEach(push); return; }
      if (!isBlank(v) && !isSkippable(v)) parts.push(String(v).trim());
    }

    push(buildIntExtName(ctx, r, category));                 // Double + name
    if (SHOW_INT_EXT_TOKEN) push(category === 'interior' ? 'Interior' : 'Exterior');
    push(buildGlass(r));                                     // Glass type
    push(buildWood(r));                                      // Wood type
    push(buildAstragal(r, category));                        // Astragal
    push(buildMeasurement(r, dim, ctx));                     // Sizes + thickness
    push(buildSidelightTransom(r, dim));                     // Sidelights / transoms
    push(buildPreHung(r, dim));                              // Pre-hung
    push(buildHinges(r, ctx));                               // Hinge finish
    push(plain(r, KEYS.locksetPrep));                        // Lockset prep
    push(buildShelf(r));                                     // Optional shelf
    push(plain(r, KEYS.panelProfile));                       // Panel profile
    var swingIE = r.raw(KEYS.swingDirection);                // Swing direction
    push(isSkippable(swingIE) ? TEXT.verifySwing : r.label(KEYS.swingDirection));
    push(buildThresholdCasing(r, category, !!ctx.isArch));   // Constant text
    push(buildDutchBolt(r, ctx));                            // Dutch door bolt

    return parts.join(' / ');
  }

  /* Spec section 3: porch panels. */
  function buildPorch(ctx) {
    var config = ctx.config || {};
    var r = makeReader(config, ctx.schema);
    var dim = makeDimReader(config, ctx.dims, r.raw(KEYS.measurementType));
    var insertRule = INSERT_RULES[norm(r.raw(KEYS.inserts))];

    var parts = [];
    function push(v) {
      if (Array.isArray(v)) { v.forEach(push); return; }
      if (!isBlank(v) && !isSkippable(v)) parts.push(String(v).trim());
    }

    push(buildPorchName(ctx, r));                            // Door name
    push(buildWood(r));                                      // Wood type
    push(insertRule ? insertRule.text : '');                 // Screen / storm function
    push(buildPorchScreenType(r, insertRule));               // Screen type
    push(buildStormInsert(r, insertRule));                   // Storm type
    push(plain(r, KEYS.frameColor));                         // Frame colour
    push(buildPorchTransom(r, dim));                         // Transom
    push(plain(r, KEYS.panelProfile));                       // Panel profile
    /* "Design Your Own" produces one line per opening; otherwise a single
     * measurement line. Read from the saved config only. */
    var openings = buildPorchOpenings(config);
    push(openings.length ? openings : buildMeasurement(r, dim, ctx));
    push(plain(r, KEYS.priming));                            // Priming

    /* The enclosure openings already carry sill / stops / astragals. Only fall
     * back to the standalone option fields when there are no openings, since
     * those fields hold UI text such as "(Change Width)". */
    if (!openings.length) {
      push(plain(r, KEYS.sill));                             // Sill
      push(plain(r, KEYS.porchAstragal));                    // Porch panel astragal
      var stops = plain(r, KEYS.porchStops);                 // Panel stops
      if (stops) push(/panel stops/i.test(stops) ? stops : 'Panel Stops: ' + stops);
    }

    return parts.join(' / ');
  }

  var api = {
    build: build,
    buildScreenStorm: buildScreenStorm,
    buildIntExt: buildIntExt,
    buildPorch: buildPorch,
    resolveCategory: resolveCategory,
    buildPorchOpenings: buildPorchOpenings,
    readEnclosureOpenings: readEnclosureOpenings,
    porchStopsAstragals: porchStopsAstragals,
    buildPorchScreenType: buildPorchScreenType,
    KEYS: KEYS,
    RULES: {
      INSERT_RULES: INSERT_RULES,
      STORM_GLASS_TEXT: STORM_GLASS_TEXT,
      MEASUREMENT_LABELS: MEASUREMENT_LABELS,
      SKIP_VALUES: SKIP_VALUES,
      WOOD_PROTECTED: WOOD_PROTECTED,
      WOOD_COVERED: WOOD_COVERED,
      TEXT: TEXT
    },
    _internal: {
      toNumber: toNumber,
      stripDoorSuffix: stripDoorSuffix,
      isSkippable: isSkippable,
      selectedText: selectedText,
      readDomPair: readDomPair,
      isZeroFrac: isZeroFrac,
      tidy: tidy,
      decimalToFraction: decimalToFraction,
      decimalToMixed: decimalToMixed,
      readConfigDim: readConfigDim,
      findByPattern: findByPattern,
      makeReader: makeReader,
      DIM_PATTERNS: DIM_PATTERNS,
      makeDimReader: makeDimReader,
      DIM_DOM: DIM_DOM,
      DIM_DOM_SHARED: DIM_DOM_SHARED
    }
  };

  if (typeof module !== 'undefined' && module.exports) module.exports = api;
  root.DoorDescription = api;
})(typeof window !== 'undefined' ? window : this);